import { useNavigate, useParams, useSearchParams } from "react-router";

import {
  Badge,
  BadgeColor,
  DetailCardSection,
  DetailList,
  DetailRow,
  formatDateTime,
  IdValue,
  Spinner,
} from "@prequel/shared-components";

import { useDatasetRecord } from "@/api/import/datasets";
import DetailPageLayout from "@/components/import/DetailPageLayout";
import { useDatasetContext } from "@/pages/import/DatasetsPage/DatasetPage";
import { getRecordStages, type RecordStage, type RecordStageStatus } from "@/pages/import/DatasetsPage/helpers";

const STAGE_BADGES: Record<RecordStageStatus, { color: BadgeColor; label: string }> = {
  success: { color: BadgeColor.GREEN, label: "Completed" },
  errored: { color: BadgeColor.RED, label: "Errored" },
  deleted: { color: BadgeColor.YELLOW, label: "Deleted" },
  pending: { color: BadgeColor.GRAY, label: "Pending" },
};

const StageCard = ({ stage }: { stage: RecordStage }) => {
  const badge = STAGE_BADGES[stage.status];

  return (
    <DetailCardSection title={stage.label}>
      <DetailList>
        <DetailRow label="Status" value={<Badge color={badge.color} text={badge.label} />} />
        {stage.at && <DetailRow label="Last run" value={formatDateTime(stage.at)} />}
        {stage.id && <DetailRow label="Job ID" value={<IdValue id={stage.id} />} />}
        {stage.errorCode && <DetailRow label="Error" value={stage.errorCode} />}
      </DetailList>
      {stage.errorTrace && (
        <pre
          className="
            mt-3 overflow-x-auto rounded-md bg-stone-50 p-3 text-xs wrap-break-word whitespace-pre-wrap text-stone-700
          "
        >
          {stage.errorTrace}
        </pre>
      )}
    </DetailCardSection>
  );
};

const DatasetRecordPage = () => {
  const navigate = useNavigate();
  const { dataset } = useDatasetContext();
  const { recordID = "" } = useParams<{ recordID: string }>();
  const [searchParams] = useSearchParams();
  const asOf = searchParams.get("as_of") ?? undefined;

  const { data: record, error, isLoading } = useDatasetRecord(dataset.id, recordID, asOf);
  const basePath = `/import/datasets/${dataset.id}`;

  return (
    <DetailPageLayout
      title={recordID}
      dynamicLabel={dataset.name}
      onGoBack={() => navigate(`${basePath}/records`)}
      subheading={
        <div className="flex items-center gap-2 text-sm text-stone-500">
          <span>{dataset.name}</span>
          {asOf && <span>as of {formatDateTime(asOf)}</span>}
        </div>
      }
    >
      <div className="mt-6">
        {/* The lookup scans the datalake and routinely takes 20s+, so say so rather than leaving
            a bare spinner on screen. */}
        {isLoading && (
          <div className="flex items-center gap-3">
            <Spinner.Inline className="text-stone-500" />
            <p className="text-sm text-stone-600">Looking this record up in the datalake. This may take a while.</p>
          </div>
        )}
        {/* The API reports a missing record as a generic 500, so a failed lookup is
            indistinguishable from a broken one. Don't claim the record doesn't exist. */}
        {!isLoading && error && (
          <p className="text-sm text-stone-600">
            Couldn&apos;t look up this record{asOf ? " at that time" : ""}. It may not exist, or the lookup may have
            failed.
          </p>
        )}
        {!isLoading && record && getRecordStages(record).map((stage) => <StageCard key={stage.key} stage={stage} />)}
      </div>
    </DetailPageLayout>
  );
};

export default DatasetRecordPage;
